import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, MapPin, Star, Wrench, ArrowRight } from 'lucide-react';
import { providerService } from '../services/providerService';
import IconLogo from '../assets/Icon_logo.svg';
import LandingHeader from '../components/LandingHeader';
import { useLanguage } from '../contexts/LanguageContext';

const ServiceProvidersPage = () => {
  const { t } = useLanguage();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('all');
  const [sortBy, setSortBy] = useState('rating');
  const [selectedProvider, setSelectedProvider] = useState(null);

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const allProviders = await providerService.getAvailableProviders();
        setProviders(allProviders);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching providers:', error);
        setLoading(false);
      }
    };

    fetchProviders();
  }, []);

  const filteredProviders = providers.filter(provider => {
    const matchesSearch = provider.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         provider.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         provider.location?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesSpecialty = selectedSpecialty === 'all' || provider.specialty === selectedSpecialty;
    return matchesSearch && matchesSpecialty;
  });

  const sortedProviders = [...filteredProviders].sort((a, b) => {
    switch (sortBy) {
      case 'rating':
        return (b.rating || 0) - (a.rating || 0);
      case 'name':
        return (a.displayName || '').localeCompare(b.displayName || '');
      case 'location':
        return (a.location || '').localeCompare(b.location || '');
      default:
        return 0;
    }
  });

  const specialties = [
    'plumbing', 'electrical', 'hvac', 'carpentry', 'painting', 
    'cleaning', 'landscaping', 'roofing', 'general'
  ];

  const getSpecialtyColor = (specialty) => {
    return 'bg-slate-100 text-slate-700 border border-slate-200';
  };

  const getSpecialtyIcon = () => (
    <Wrench className="w-5 h-5 text-slate-300" strokeWidth={1.5} />
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <LandingHeader />

      <section className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6">
            {t('serviceProvidersPage.hero.title')}
            <span className="block text-slate-200 mt-2">
              {t('serviceProvidersPage.hero.subtitle')}
            </span>
          </h1>
          <p className="text-xl lg:text-2xl text-slate-300 max-w-3xl mx-auto mb-8">
            {t('serviceProvidersPage.hero.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" className="bg-slate-600 text-white px-8 py-4 text-lg font-semibold rounded-xl hover:bg-slate-500 transition-all duration-300 shadow-lg">
              {t('serviceProvidersPage.hero.joinAsOwner')}
            </Link>
            <Link to="/register" className="border-2 border-slate-400/50 text-white px-8 py-4 text-lg font-semibold rounded-xl hover:bg-white/10 hover:border-slate-300 transition-all duration-300">
              {t('serviceProvidersPage.hero.becomeProvider')}
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-700 mb-2">500+</div>
              <div className="text-slate-600">Verified Providers</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-700 mb-2">50+</div>
              <div className="text-slate-600">Service Categories</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-700 mb-2">4.8★</div>
              <div className="text-slate-600">Average Rating</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-slate-700 mb-2">24/7</div>
              <div className="text-slate-600">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow-sm border border-slate-200 p-8 rounded-xl">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="md:col-span-2 relative">
                <label className="block text-sm font-medium text-slate-700 mb-2">Search Providers</label>
                <Search className="absolute left-3 top-[2.6rem] w-4 h-4 text-slate-400" strokeWidth={1.75} />
                <input
                  type="text"
                  placeholder={t('serviceProvidersPage.search.placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 focus:ring-2 focus:ring-slate-300 focus:border-slate-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Specialty</label>
                <select
                  value={selectedSpecialty}
                  onChange={(e) => setSelectedSpecialty(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-slate-300 focus:border-slate-300 rounded-lg text-sm"
                >
                  <option value="all">{t('serviceProvidersPage.specialty.all')}</option>
                  {specialties.map(specialty => (
                    <option key={specialty} value={specialty}>
                      {specialty.charAt(0).toUpperCase() + specialty.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-4 py-3 border border-slate-200 focus:ring-2 focus:ring-slate-300 focus:border-slate-300 rounded-lg text-sm"
                >
                  <option value="rating">{t('serviceProvidersPage.sort.rating')}</option>
                  <option value="name">{t('serviceProvidersPage.sort.name')}</option>
                  <option value="location">{t('serviceProvidersPage.sort.location')}</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Providers Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-16 w-16 border-2 border-slate-200 border-t-slate-600 mx-auto mb-6" />
              <p className="text-xl text-slate-600">{t('serviceProvidersPage.loading')}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {sortedProviders.map((provider) => (
                  <div key={provider.id} className="relative bg-white shadow-sm border border-slate-200 p-8 rounded-xl hover:border-slate-300 hover:shadow-md transition-all duration-200 cursor-pointer" onClick={() => setSelectedProvider(provider)}>
                    <div className="absolute top-6 right-6 opacity-30">
                      {getSpecialtyIcon()}
                    </div>
                    <div className="mb-6">
                      <h3 className="text-xl font-bold text-slate-800 mb-3">{provider.displayName}</h3>
                      <div className="flex items-center gap-3 mb-4 flex-wrap">
                        <span className={`px-3 py-1.5 text-sm font-medium rounded-lg border ${getSpecialtyColor(provider.specialty)}`}>
                          {provider.specialty}
                        </span>
                        {provider.rating && (
                          <div className="flex items-center gap-1">
                            <Star className="w-5 h-5 text-slate-500 fill-slate-200" strokeWidth={1.5} />
                            <span className="text-sm font-semibold text-slate-700">{provider.rating}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-4 mb-6">
                      {provider.description && (
                        <div>
                          <h4 className="text-sm font-medium text-slate-700 mb-2">{t('serviceProvidersPage.provider.servicesOffered')}</h4>
                          <p className="text-slate-600 leading-relaxed text-sm">{provider.description}</p>
                        </div>
                      )}
                      {provider.location && (
                        <div className="flex items-center gap-2 text-slate-500 text-sm">
                          <MapPin className="w-4 h-4 flex-shrink-0" strokeWidth={1.75} />
                          <span className="font-medium">{provider.location}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <button className="flex-1 px-4 py-3 bg-slate-700 text-white text-sm font-medium hover:bg-slate-800 transition-colors text-center rounded-lg">
                        {t('serviceProvidersPage.provider.contactProvider')}
                      </button>
                      <button className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 transition-colors text-center rounded-lg">
                        {t('serviceProvidersPage.provider.viewProfile')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {!loading && sortedProviders.length === 0 && (
                <div className="text-center py-20">
                  <div className="w-20 h-20 bg-slate-100 flex items-center justify-center mx-auto mb-6 rounded-full">
                    <Search className="w-10 h-10 text-slate-400" strokeWidth={1.25} />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-800 mb-4">{t('serviceProvidersPage.noProvidersFound.title')}</h3>
                  <p className="text-slate-600 text-lg mb-8">{t('serviceProvidersPage.noProvidersFound.description')}</p>
                  <Link to="/register" className="inline-block bg-slate-700 text-white px-8 py-3 text-lg font-semibold rounded-lg hover:bg-slate-800 transition-colors">
                    {t('serviceProvidersPage.noProvidersFound.joinAsProvider')}
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="border-t border-amber-200 bg-amber-400 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold tracking-tight text-amber-950 sm:text-4xl">
            {t('serviceProvidersPage.cta.title')}
          </h2>
          <p className="mt-5 text-lg text-amber-900/90">
            {t('serviceProvidersPage.cta.description')}
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/register"
              className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-amber-950 px-8 py-4 text-base font-semibold text-amber-50 shadow-lg transition hover:bg-amber-900 sm:w-auto"
            >
              {t('serviceProvidersPage.cta.getStarted')}
              <ArrowRight className="h-5 w-5" strokeWidth={2} />
            </Link>
            <Link
              to="/features"
              className="inline-flex w-full items-center justify-center rounded-xl border-2 border-amber-800 px-8 py-4 text-base font-semibold text-amber-950 transition hover:bg-amber-500/30 sm:w-auto"
            >
              {t('serviceProvidersPage.cta.learnMore')}
            </Link>
          </div>
        </div>
      </section>

      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <img className="h-16 w-auto mb-4" src={IconLogo} alt="EasyProperty" />
              <p className="text-slate-400">
                {t('footer.description')}
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4 text-slate-100">{t('footer.platform')}</h3>
              <ul className="space-y-2 text-slate-400">
                <li><Link to="/register" className="hover:text-white">{t('footer.signUp')}</Link></li>
                <li><Link to="/login" className="hover:text-white">{t('footer.signIn')}</Link></li>
                <li><Link to="/providers" className="hover:text-white">{t('footer.serviceProviders')}</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4 text-slate-100">{t('footer.support')}</h3>
              <ul className="space-y-2 text-slate-400">
                <li><button className="hover:text-white text-left">{t('footer.helpCenter')}</button></li>
                <li><button className="hover:text-white text-left">{t('footer.contactUs')}</button></li>
                <li><button className="hover:text-white text-left">{t('footer.privacyPolicy')}</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4 text-slate-100">{t('footer.connect')}</h3>
              <ul className="space-y-2 text-slate-400">
                <li><button className="hover:text-white text-left">{t('footer.aboutUs')}</button></li>
                <li><button className="hover:text-white text-left">{t('footer.blog')}</button></li>
                <li><button className="hover:text-white text-left">{t('footer.careers')}</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-slate-400">
            <p>&copy; 2024 EasyProperty. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ServiceProvidersPage; 